import { appThemeColors, editorThemeVariables } from "../theme";
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  mountOverlayEditorControls,
  OverlayEditorSession,
  type OverlayEditorPanel,
  OverlayRenderer,
  type OverlayAnnotation,
  type OverlayGeoJsonAnnotation,
} from "@impleotv-pm/st-overlay/editor";
import "@impleotv-pm/st-overlay/styles/editor.css";
import { useTheme } from "@mui/material/styles";
import {
  createDefaultRulerPoints,
  denormalizePlayerOverlayLayout,
  normalizePlayerOverlayLayout,
  type StoredPlayerOverlayLayout,
} from "../utils/playerOverlayLayout";
import type { AnnotationRenderItem } from "../types/annotation";

type RawKlvPacket = {
  time: Date;
  klv: Record<string, unknown>;
} | null;

type OverlayEditorSessionPanelProps = {
  klvPacket: RawKlvPacket;
  layout: StoredPlayerOverlayLayout | null;
  onLayoutChange: (layout: StoredPlayerOverlayLayout) => void;
  onMarkerStateChange?: (state: OverlayMarkerStateSnapshot) => void;
  overlaySurfaceElement?: HTMLDivElement | null;
  gridEnabled?: boolean;
  rulerEnabled?: boolean;
  markerEnabled?: boolean;
  annotations?: AnnotationRenderItem[];
};

type GeoPoint = {
  latitude: number;
  longitude: number;
};

type OverlayMarkerStateSnapshot = {
  enabled: boolean;
  master: boolean;
  available: boolean;
  point: { x: number; y: number } | null;
  latitude: number | null;
  longitude: number | null;
};

const overlayRootStyle = {
  position: "absolute",
  inset: 0,
} satisfies CSSProperties;

const cloneOverlayGeoJson = (value: OverlayGeoJsonAnnotation): OverlayGeoJsonAnnotation =>
  JSON.parse(JSON.stringify(value)) as OverlayGeoJsonAnnotation;

const syncRendererAnnotations = (renderer: OverlayRenderer, annotations: AnnotationRenderItem[]) => {
  for (const annotation of renderer.getAnnotations()) {
    renderer.removeAnnotation(annotation.id);
  }

  const nextAnnotations: OverlayAnnotation[] = annotations.map((annotation) => ({
    id: annotation.id,
    name: annotation.name,
    geojson: cloneOverlayGeoJson(annotation.geojson as OverlayGeoJsonAnnotation),
    show: true,
    selected: false,
  }));

  if (nextAnnotations.length > 0) {
    renderer.addAnnotations(nextAnnotations);
  }
};

const getNumericField = (
  fields: Record<string, unknown> | null | undefined,
  ...keys: string[]
) => {
  if (!fields) {
    return null;
  }

  for (const key of keys) {
    const value = fields[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return null;
};

const normalizeHeading = (value: number | null) => {
  if (value === null || !Number.isFinite(value)) {
    return null;
  }

  if (value >= 0 && value <= 360) {
    return value;
  }

  const normalized = ((value % 360) + 360) % 360;
  return normalized === 0 && value > 0 ? 360 : normalized;
};

const toRadians = (value: number) => (value * Math.PI) / 180;
const toDegrees = (value: number) => (value * 180) / Math.PI;

const midpoint = (first: GeoPoint, second: GeoPoint): GeoPoint => ({
  latitude: (first.latitude + second.latitude) / 2,
  longitude: (first.longitude + second.longitude) / 2,
});

const bearingBetween = (from: GeoPoint, to: GeoPoint) => {
  const latitude1 = toRadians(from.latitude);
  const latitude2 = toRadians(to.latitude);
  const deltaLongitude = toRadians(to.longitude - from.longitude);

  const y = Math.sin(deltaLongitude) * Math.cos(latitude2);
  const x =
    Math.cos(latitude1) * Math.sin(latitude2) -
    Math.sin(latitude1) * Math.cos(latitude2) * Math.cos(deltaLongitude);

  if (Math.abs(x) < Number.EPSILON && Math.abs(y) < Number.EPSILON) {
    return null;
  }

  return normalizeHeading(toDegrees(Math.atan2(y, x)));
};

const resolvePoint = (
  fields: Record<string, unknown> | null | undefined,
  latitudeKeys: string[],
  longitudeKeys: string[],
): GeoPoint | null => {
  const latitude = getNumericField(fields, ...latitudeKeys);
  const longitude = getNumericField(fields, ...longitudeKeys);
  if (latitude === null || longitude === null) {
    return null;
  }

  return { latitude, longitude };
};

const resolveFrameCenter = (
  packet: Record<string, unknown> | null | undefined,
) =>
  resolvePoint(
    packet,
    ["23", "FrameCenterLatitude", "Frame Center Latitude"],
    ["24", "FrameCenterLongitude", "Frame Center Longitude"],
  );

const resolveSensorPosition = (
  packet: Record<string, unknown> | null | undefined,
) =>
  resolvePoint(
    packet,
    ["13", "SensorLatitude", "Sensor Latitude"],
    ["14", "SensorLongitude", "Sensor Longitude"],
  );

const resolveOffsetCornerPoint = (
  packet: Record<string, unknown> | null | undefined,
  frameCenter: GeoPoint | null,
  latitudeKeys: string[],
  longitudeKeys: string[],
) => {
  if (!frameCenter) {
    return null;
  }

  const latitudeOffset = getNumericField(packet, ...latitudeKeys);
  const longitudeOffset = getNumericField(packet, ...longitudeKeys);
  if (latitudeOffset === null || longitudeOffset === null) {
    return null;
  }

  return {
    latitude: frameCenter.latitude + latitudeOffset,
    longitude: frameCenter.longitude + longitudeOffset,
  };
};

const resolveTerrainCorners = (
  packet: Record<string, unknown> | null | undefined,
) => {
  const frameCenter = resolveFrameCenter(packet);
  const fullCorners = [
    resolvePoint(
      packet,
      ["82", "CornerLatitudePoint1Full", "Corner Latitude Point 1 (Full)"],
      ["83", "CornerLongitudePoint1Full", "Corner Longitude Point 1 (Full)"],
    ),
    resolvePoint(
      packet,
      ["84", "CornerLatitudePoint2Full", "Corner Latitude Point 2 (Full)"],
      ["85", "CornerLongitudePoint2Full", "Corner Longitude Point 2 (Full)"],
    ),
    resolvePoint(
      packet,
      ["86", "CornerLatitudePoint3Full", "Corner Latitude Point 3 (Full)"],
      ["87", "CornerLongitudePoint3Full", "Corner Longitude Point 3 (Full)"],
    ),
    resolvePoint(
      packet,
      ["88", "CornerLatitudePoint4Full", "Corner Latitude Point 4 (Full)"],
      ["89", "CornerLongitudePoint4Full", "Corner Longitude Point 4 (Full)"],
    ),
  ];

  if (fullCorners.every(Boolean)) {
    return fullCorners as GeoPoint[];
  }

  const offsetCorners = [
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["26", "OffsetCornerLatitudePoint1", "Offset Corner Latitude Point 1"],
      ["27", "OffsetCornerLongitudePoint1", "Offset Corner Longitude Point 1"],
    ),
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["28", "OffsetCornerLatitudePoint2", "Offset Corner Latitude Point 2"],
      ["29", "OffsetCornerLongitudePoint2", "Offset Corner Longitude Point 2"],
    ),
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["30", "OffsetCornerLatitudePoint3", "Offset Corner Latitude Point 3"],
      ["31", "OffsetCornerLongitudePoint3", "Offset Corner Longitude Point 3"],
    ),
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["32", "OffsetCornerLatitudePoint4", "Offset Corner Latitude Point 4"],
      ["33", "OffsetCornerLongitudePoint4", "Offset Corner Longitude Point 4"],
    ),
  ];

  if (offsetCorners.every(Boolean)) {
    return offsetCorners as GeoPoint[];
  }

  return null;
};

const resolveTerrainOrientation = (
  packet: Record<string, unknown> | null | undefined,
) => {
  const corners = resolveTerrainCorners(packet);
  if (corners) {
    const topMidpoint = midpoint(corners[0], corners[1]);
    const bottomMidpoint = midpoint(corners[3], corners[2]);
    const cornerBearing = bearingBetween(bottomMidpoint, topMidpoint);
    if (cornerBearing !== null) {
      return cornerBearing;
    }
  }

  const sensorPosition = resolveSensorPosition(packet);
  const frameCenter = resolveFrameCenter(packet);
  if (sensorPosition && frameCenter) {
    return bearingBetween(sensorPosition, frameCenter);
  }

  return null;
};

const resolvePlatformHeading = (
  packet: Record<string, unknown> | null | undefined,
) =>
  normalizeHeading(
    getNumericField(
      packet,
      "5",
      "PlatformHeadingAngle",
      "Platform Heading Angle",
      "PlatformHeading",
    ),
  );

const OverlayEditorSessionPanel = ({
  klvPacket,
  layout,
  onLayoutChange,
  onMarkerStateChange,
  overlaySurfaceElement,
  gridEnabled = false,
  rulerEnabled = false,
  markerEnabled = false,
  annotations = [],
}: OverlayEditorSessionPanelProps) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overlayRootRef = useRef<HTMLDivElement | null>(null);
  const controlsRootRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<OverlayRenderer | null>(null);
  const editorRef = useRef<OverlayEditorSession | null>(null);
  const panelRef = useRef<OverlayEditorPanel | null>(null);
  const publishedLayoutRef = useRef<StoredPlayerOverlayLayout | null>(null);
  const pendingPersistFrameRef = useRef<number | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [lockedPacket, setLockedPacket] = useState<Record<string, unknown> | null>(
    () => klvPacket?.klv ?? null,
  );

  const packet = klvPacket?.klv ?? null;
  const effectivePacket = lockedPacket ?? packet;
  const overlayTheme = theme.palette.mode === "light" ? "light" : "dark";
  const heading = useMemo(() => resolvePlatformHeading(effectivePacket) ?? 0, [effectivePacket]);
  const terrainOrientation = useMemo(
    () => resolveTerrainOrientation(effectivePacket) ?? 0,
    [effectivePacket],
  );
  const latestLayoutRef = useRef<StoredPlayerOverlayLayout | null>(layout);
  const latestPacketStateRef = useRef({
    packet: effectivePacket,
    heading,
    terrainOrientation,
  });

  latestLayoutRef.current = layout;
  latestPacketStateRef.current = {
    packet: effectivePacket,
    heading,
    terrainOrientation,
  };

  useEffect(() => {
    if (!lockedPacket && packet) {
      setLockedPacket(packet);
    }
  }, [lockedPacket, packet]);

  const cancelPendingPersist = useCallback(() => {
    if (pendingPersistFrameRef.current !== null) {
      window.cancelAnimationFrame(pendingPersistFrameRef.current);
      pendingPersistFrameRef.current = null;
    }
  }, []);

  const emitMarkerStateChange = useCallback((editor = editorRef.current) => {
    if (!onMarkerStateChange) {
      return;
    }

    if (!editor) {
      onMarkerStateChange({
        enabled: false,
        master: true,
        available: false,
        point: null,
        latitude: null,
        longitude: null,
      });
      return;
    }

    onMarkerStateChange(editor.getMarkerState());
  }, [onMarkerStateChange]);

  const persistCurrentLayout = useCallback((editor = editorRef.current) => {
    if (!editor || size.width < 1 || size.height < 1) {
      return;
    }

    const nextLayout = normalizePlayerOverlayLayout(editor.serializeLayout(), size);
    publishedLayoutRef.current = nextLayout;
    onLayoutChange(nextLayout);
  }, [onLayoutChange, size]);

  const schedulePersistCurrentLayout = useCallback((editor = editorRef.current) => {
    if (!editor || size.width < 1 || size.height < 1) {
      cancelPendingPersist();
      return;
    }

    cancelPendingPersist();
    pendingPersistFrameRef.current = window.requestAnimationFrame(() => {
      pendingPersistFrameRef.current = null;
      persistCurrentLayout(editor);
    });
  }, [cancelPendingPersist, persistCurrentLayout, size.height, size.width]);

  useEffect(() => cancelPendingPersist, [cancelPendingPersist]);

  const ensureDefaultRulerState = useCallback((editor = editorRef.current) => {
    if (!editor || size.width < 1 || size.height < 1) {
      return false;
    }

    const currentState = editor.getRulerState();
    if (currentState.start && currentState.end) {
      return false;
    }

    editor.loadRulerState(createDefaultRulerPoints(size));
    return true;
  }, [size]);

  useEffect(() => {
    const element = overlaySurfaceElement ?? containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const nextWidth = Math.round(entry.contentRect.width);
      const nextHeight = Math.round(entry.contentRect.height);
      setSize((current) =>
        current.width === nextWidth && current.height === nextHeight
          ? current
          : { width: nextWidth, height: nextHeight },
      );
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [overlaySurfaceElement]);

  useEffect(() => {
    const overlayElement = overlaySurfaceElement ?? overlayRootRef.current;
    const controlsElement = controlsRootRef.current;
    if (!overlayElement || !controlsElement || size.width < 1 || size.height < 1) {
      return;
    }

    controlsElement.replaceChildren();

    const renderer = new OverlayRenderer({
      container: overlayElement,
      width: size.width,
      height: size.height,
    });
    const editor = new OverlayEditorSession(renderer, {
      defaultNewDataTextPosition: { x: 16, y: 202, stepY: 28 },
    });
    const panel = mountOverlayEditorControls(controlsElement, editor);

    rendererRef.current = renderer;
    editorRef.current = editor;
    panelRef.current = panel;

    editor.setEnabled(true);

    const currentLayout = latestLayoutRef.current;
    if (currentLayout) {
      editor.loadLayout(denormalizePlayerOverlayLayout(currentLayout, size));
    }
    syncRendererAnnotations(renderer, annotations);
    editor.setLayerEnabled("grid", gridEnabled);
    editor.setRulerEnabled(rulerEnabled);
    editor.setMarkerEnabled(markerEnabled);

    const currentPacketState = latestPacketStateRef.current;
    renderer.updatePacket({
      klv: currentPacketState.packet,
      vmti: null,
      heading: currentPacketState.heading,
      terrainOrientation: currentPacketState.terrainOrientation,
    });
    emitMarkerStateChange(editor);

    const syncPersist = (deferred = false) => {
      if (deferred) {
        schedulePersistCurrentLayout(editor);
      } else {
        cancelPendingPersist();
        persistCurrentLayout(editor);
      }

      window.requestAnimationFrame(() => {
        panelRef.current?.refresh();
      });
    };

    const unsubscribeChanges = editor.subscribeChanges((event: { reason: string }) => {
      if (event.reason === "marker") {
        emitMarkerStateChange(editor);
      }
      syncPersist(event.reason === "layout");
    });

    window.requestAnimationFrame(() => {
      if (!editor.getSelectedObjectId()) {
        editor.selectObject("compass");
      }
      panel.refresh();
    });

    return () => {
      cancelPendingPersist();
      persistCurrentLayout(editor);
      unsubscribeChanges();
      panel.destroy();
      renderer.destroy();
      rendererRef.current = null;
      editorRef.current = null;
      panelRef.current = null;
      controlsElement.replaceChildren();
      emitMarkerStateChange(null);
    };
  }, [cancelPendingPersist, emitMarkerStateChange, gridEnabled, markerEnabled, overlaySurfaceElement, overlayTheme, persistCurrentLayout, rulerEnabled, schedulePersistCurrentLayout, size.height, size.width]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !layout || size.width < 1 || size.height < 1) {
      return;
    }

    // A saved drag comes back through React props. Re-loading that echo would
    // interrupt the next interaction; only external layout changes need loading.
    if (publishedLayoutRef.current === layout) {
      return;
    }

    editor.loadLayout(denormalizePlayerOverlayLayout(layout, size));
    emitMarkerStateChange(editor);
    panelRef.current?.refresh();
  }, [emitMarkerStateChange, layout, size.height, size.width]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || editor.isLayerEnabled("grid") === gridEnabled) {
      return;
    }

    editor.setLayerEnabled("grid", gridEnabled);
    panelRef.current?.refresh();
  }, [gridEnabled]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || editor.isRulerEnabled() === rulerEnabled) {
      return;
    }

    editor.setRulerEnabled(rulerEnabled);
    panelRef.current?.refresh();
  }, [rulerEnabled]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || editor.isMarkerEnabled() === markerEnabled) {
      return;
    }

    editor.setMarkerEnabled(markerEnabled);
    emitMarkerStateChange(editor);
    persistCurrentLayout(editor);
    panelRef.current?.refresh();
  }, [emitMarkerStateChange, markerEnabled, persistCurrentLayout]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !rulerEnabled) {
      return;
    }

    if (!ensureDefaultRulerState(editor)) {
      return;
    }

    persistCurrentLayout(editor);
    panelRef.current?.refresh();
  }, [ensureDefaultRulerState, persistCurrentLayout, rulerEnabled]);

  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer) {
      return;
    }

    renderer.updatePacket({
      klv: effectivePacket,
      vmti: null,
      heading,
      terrainOrientation,
    });
    emitMarkerStateChange(editorRef.current);
    panelRef.current?.refresh();
  }, [effectivePacket, emitMarkerStateChange, heading, terrainOrientation]);

  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer) {
      return;
    }

    syncRendererAnnotations(renderer, annotations);
    panelRef.current?.refresh();
  }, [annotations]);

  const overlayFrameStyle = useMemo<CSSProperties>(() => {
    const colors = appThemeColors[overlayTheme];
    return {
      position: "relative", width: "100%", aspectRatio: "16 / 9",
      border: `1px solid ${colors.border}`, borderRadius: "12px", overflow: "hidden",
      background: colors.background,
    };
  }, [overlayTheme]);

  return (
    <section data-st-overlay-theme={overlayTheme} style={editorThemeVariables(overlayTheme)}>
      <div ref={controlsRootRef}></div>
      {overlaySurfaceElement ? null : (
        <div
          ref={containerRef}
          className="frame"
          aria-label="Overlay sample surface"
          style={overlayFrameStyle}
        >
          <div ref={overlayRootRef} id="overlay-root" style={overlayRootStyle}></div>
        </div>
      )}
    </section>
  );
};

export default OverlayEditorSessionPanel;
